<template>
  <div @focusout="leave">
    <label for="captain-search" class="form-label text-base"
      >Team Captain</label
    >
    <div v-if="current" class="mt-3">
      <p class="text-gray-600">Current captain</p>
      <p class="text-lg font-bold mt-1">{{ current.name || current.email }}</p>
      <p class="text-gray-600 break-words">{{ current.email }}</p>
      <p v-if="!current.active" class="text-gray-600">Inactive account</p>
    </div>
    <p v-else-if="!loading && !error" class="mt-2 text-gray-600">
      No current captain found.
    </p>
    <div
      v-if="selected && selected.id !== (current && current.id)"
      class="mt-3"
      role="status"
    >
      <p class="flex items-center gap-2 text-gray-600 mb-3">
        <span aria-hidden="true" class="text-2xl">↓</span> Will change to
      </p>
      <div class="border border-theme-1 rounded-md p-3 bg-gray-100">
        <div class="flex items-center justify-between gap-2">
          <p class="font-medium">New captain</p>
          <button
            type="button"
            class="underline text-gray-600"
            :disabled="disabled"
            aria-label="Cancel captain selection"
            @click="clearSelection"
          >
            Cancel ×
          </button>
        </div>
        <p class="text-lg font-bold mt-1">
          {{ selected.name || selected.email }}
        </p>
        <p class="text-gray-600 break-words">{{ selected.email }}</p>
        <p class="text-gray-600 mt-2">
          Select Update Captain to save this change.
        </p>
      </div>
    </div>
    <input
      id="captain-search"
      v-model="search"
      type="search"
      role="combobox"
      aria-autocomplete="list"
      aria-controls="captain-options"
      :aria-expanded="open"
      :aria-activedescendant="
        highlight >= 0 && results[highlight]
          ? `captain-option-${results[highlight].id}`
          : undefined
      "
      :disabled="disabled"
      class="form-control mt-4"
      placeholder="Search active users by name or email…"
      autocomplete="off"
      maxlength="200"
      @focus="focusSearch"
      @keydown.down.prevent="move(1)"
      @keydown.up.prevent="move(-1)"
      @keydown.enter.prevent="chooseHighlighted"
      @keydown.esc="open = false"
    />
    <div v-if="open" class="box border p-3 mt-2">
      <p v-if="loading" role="status">Searching active users…</p>
      <p v-if="error" role="alert" class="text-red">
        {{ error }}
        <button
          type="button"
          class="underline"
          :disabled="loading"
          @click="retry"
        >
          Try again
        </button>
      </p>
      <p v-else-if="!loading && !results.length" role="status">
        No active users match this search.
      </p>
      <ul
        id="captain-options"
        role="listbox"
        aria-label="Active captain options"
      >
        <li
          v-for="(user, index) in results"
          :id="`captain-option-${user.id}`"
          :key="user.id"
          role="option"
          :aria-selected="highlight === index"
          :class="{ 'bg-gray-100': highlight === index }"
        >
          <button
            type="button"
            class="block w-full text-left p-2"
            :disabled="disabled || loading"
            @mousedown.prevent
            @click="choose(user)"
          >
            <span class="font-medium">{{ user.name || user.email }}</span
            ><span class="block text-gray-600">{{ user.email }}</span>
          </button>
        </li>
      </ul>
      <div v-if="totalPages > 1" class="flex items-center gap-3 mt-2">
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading || page <= 1"
          @click="changePage(page - 1)"
        >
          Previous</button
        ><span>{{ page }} / {{ totalPages }}</span>
        <button
          type="button"
          class="btn btn-outline-secondary"
          :disabled="loading || page >= totalPages"
          @click="changePage(page + 1)"
        >
          Next
        </button>
      </div>
      <p v-if="!loading && !error" class="text-gray-600 mt-2">
        {{ total }} matching active users
      </p>
    </div>
    <p v-if="error && !open" class="mt-2 text-red" role="alert">
      {{ error }}
      <button type="button" class="underline" @click="retry">Try again</button>
    </p>
  </div>
</template>
<script>
import { defineComponent, ref, watch, onMounted, onUnmounted } from "vue";
import { getCaptainOptions } from "@/api/captain-options";
import { errorMessage } from "@/composables/use-admin-list";

export default defineComponent({
  props: {
    modelValue: { type: String, default: "" },
    teamId: { type: String, required: true },
    disabled: Boolean
  },
  emits: ["update:modelValue", "ready"],
  setup(props, { emit }) {
    const current = ref(null);
    const selected = ref(null);
    const search = ref("");
    const open = ref(false);
    const results = ref([]);
    const page = ref(1);
    const total = ref(0);
    const totalPages = ref(0);
    const highlight = ref(-1);
    const loading = ref(false);
    const error = ref("");
    let disposed = false;
    let revision = 0;
    let timer;
    let initialized = false;
    let clearingSearch = false;
    const load = async (withCurrent = false) => {
      const stamp = ++revision;
      loading.value = true;
      error.value = "";
      highlight.value = -1;
      try {
        const response = await getCaptainOptions({
          search: search.value.trim(),
          page: page.value,
          ...(withCurrent ? { teamId: props.teamId } : {})
        });
        if (disposed || stamp !== revision) return;
        results.value = response.results;
        total.value = response.totalResults;
        totalPages.value = response.totalPages;
        if (withCurrent) {
          current.value = response.current;
          initialized = true;
        }
      } catch (err) {
        if (!disposed && stamp === revision) {
          error.value = errorMessage(err);
          results.value = [];
        }
      } finally {
        if (!disposed && stamp === revision) loading.value = false;
      }
    };
    const choose = user => {
      if (props.disabled || loading.value || !user) return;
      selected.value = user;
      emit("update:modelValue", user.id);
      emit(
        "ready",
        user.active !== false && user.id !== (current.value && current.value.id)
      );
      open.value = false;
    };
    const clearSelection = () => {
      if (props.disabled) return;
      clearTimeout(timer);
      revision++;
      selected.value = null;
      results.value = [];
      page.value = 1;
      total.value = 0;
      totalPages.value = 0;
      loading.value = false;
      error.value = "";
      open.value = false;
      clearingSearch = search.value !== "";
      search.value = "";
      emit("update:modelValue", current.value ? current.value.id : "");
      emit("ready", false);
    };
    const focusSearch = () => {
      open.value = true;
      if (!results.value.length && !loading.value) load(!initialized);
    };
    const move = direction => {
      open.value = true;
      if (!results.value.length || loading.value) return;
      highlight.value = Math.max(
        0,
        Math.min(results.value.length - 1, highlight.value + direction)
      );
    };
    const chooseHighlighted = () => {
      if (open.value && highlight.value >= 0)
        choose(results.value[highlight.value]);
    };
    const leave = event => {
      if (!event.currentTarget.contains(event.relatedTarget))
        open.value = false;
    };
    const changePage = next => {
      if (!loading.value) {
        page.value = next;
        load(!initialized);
      }
    };
    const retry = () => load(!initialized);
    const initialize = () => {
      clearTimeout(timer);
      initialized = false;
      current.value = null;
      selected.value = null;
      page.value = 1;
      results.value = [];
      emit("ready", false);
      load(true);
    };
    watch(search, () => {
      if (clearingSearch) {
        clearingSearch = false;
        return;
      }
      clearTimeout(timer);
      revision++;
      loading.value = true;
      results.value = [];
      page.value = 1;
      open.value = true;
      selected.value = null;
      emit("ready", false);
      timer = setTimeout(() => load(!initialized), 300);
    });
    watch(() => props.teamId, initialize);
    onMounted(initialize);
    onUnmounted(() => {
      disposed = true;
      revision++;
      clearTimeout(timer);
    });
    return {
      current,
      selected,
      search,
      open,
      results,
      page,
      total,
      totalPages,
      highlight,
      loading,
      error,
      choose,
      clearSelection,
      focusSearch,
      move,
      chooseHighlighted,
      leave,
      changePage,
      retry
    };
  }
});
</script>
